import type { QueryClient } from '@tanstack/react-query';

/**
 * Throttle for storm-prone forced refetches triggered by WebSocket events.
 *
 * Background: `applyEvent.ts` reconciles optimistic store state with the
 * server by invalidating (force-refetching) queries after a 2s debounce
 * window. That debounce collapses one *burst* of events into one flush — but
 * during sustained event streams (e.g. meeting enrichment writing property
 * updates for tens of seconds), a flush fires every ~2s and each flush
 * re-forces a refetch of the same broad set of observed queries. With
 * 3-12s server times on crm-objects/query/, this produced 9-10 identical
 * refetches of dashboard/list queries within ~35s in production (and 18
 * aggregate refetches) while a record page loaded.
 *
 * This module adds the missing second layer: a per-query cooldown on
 * *forced* refetches.
 *
 * - The FIRST invalidation of a query in a quiet period passes through
 *   immediately — a user editing a property still sees the confirmation
 *   refetch right away.
 * - Repeat invalidations of the same query within the cooldown are
 *   suppressed, and the query hash is queued.
 * - One trailing catch-up invalidation fires when the cooldown expires, so
 *   every suppressed query still reconciles with the server after the storm
 *   quiets. Bounded staleness: a query is never more than
 *   FORCED_REFETCH_COOLDOWN_MS behind, and the store (fed synchronously by
 *   the WebSocket) keeps the visible object data live in the meantime.
 *
 * This throttles only the reconciliation refetch frequency — it never
 * changes WHICH queries are invalidated (predicate semantics stay with the
 * callers in applyEvent.ts).
 */
export const FORCED_REFETCH_COOLDOWN_MS = 15_000;

/**
 * Shorter cooldown for surfaces where the user directly watches the result
 * of their own action land (e.g. the record timeline showing an edit's
 * event). Still collapses the ~2s-per-flush storm, but a rapid second edit
 * reconciles within a few seconds instead of 15.
 */
export const INTERACTIVE_REFETCH_COOLDOWN_MS = 5_000;

interface ThrottleEntry {
  /** Time before which forced refetches of this query are suppressed. */
  allowedAgainAt: number;
  /** Cooldown used by the call site — reused when catch-up re-stamps. */
  cooldownMs: number;
}

/**
 * Per-query-hash throttle state. Bounded by the number of distinct queries
 * the invalidators ever match (small — observed queries on screen), so no
 * explicit pruning is needed.
 */
let throttleEntries = new Map<string, ThrottleEntry>();

/** Query hashes suppressed during the current cooldown, awaiting catch-up. */
let suppressedHashes = new Set<string>();

/**
 * Hashes granted a forced refetch during the current invalidation batch (one
 * applyEvent flush), or null when no batch is open. Opened by
 * `beginRefetchBatch` and closed by `endRefetchBatch`.
 *
 * Within a single flush, multiple invalidators can match the SAME query — a
 * privacy-affecting association queues both `invalidateListQueries` and
 * `invalidateObjectQueries`, which both cover that type's list keys. The first
 * matcher is allowed and the query refetches now; a second matcher for the same
 * hash must NOT queue a trailing catch-up, because the query is already
 * reconciling in this very flush and a ~15s-later refetch would be pure waste.
 * Cross-flush suppression (the real storm case) is unaffected: a hash allowed
 * in a PRIOR flush is not present in the current batch's set.
 */
let allowedThisBatch: Set<string> | null = null;

let catchUpTimer: ReturnType<typeof setTimeout> | null = null;
/** Absolute time (ms) the currently scheduled catch-up timer will fire. */
let catchUpFireAt = 0;
let catchUpQueryClient: QueryClient | null = null;

/**
 * Decide whether a forced refetch of `queryHash` may proceed now.
 *
 * Call from an `invalidateQueries` predicate as the FINAL condition (after
 * all key-matching checks), because a `true` return records the refetch
 * timestamp:
 *
 *   return keyMatches && allowForcedRefetch(queryClient, query.queryHash);
 *
 * Returns false when the query was force-refetched within the cooldown; the
 * hash is queued and a single trailing catch-up invalidation is scheduled.
 */
export function allowForcedRefetch(
  queryClient: QueryClient,
  queryHash: string | undefined,
  cooldownMs: number = FORCED_REFETCH_COOLDOWN_MS
): boolean {
  // Real Query instances always carry a queryHash; a missing hash means a
  // synthetic query object (tests invoking predicates directly). Allow the
  // refetch rather than throttling every hashless caller as one identity.
  if (!queryHash) {
    return true;
  }
  const now = Date.now();
  const entry = throttleEntries.get(queryHash);
  if (entry !== undefined && now < entry.allowedAgainAt) {
    // Already granted a refetch earlier in this same flush — the query is
    // reconciling now, so a trailing catch-up would be a redundant refetch of
    // data that just arrived. Suppress without queuing one.
    if (allowedThisBatch?.has(queryHash)) {
      return false;
    }
    suppressedHashes.add(queryHash);
    scheduleCatchUp(queryClient, entry.allowedAgainAt - now);
    return false;
  }
  throttleEntries.set(queryHash, {
    allowedAgainAt: now + cooldownMs,
    cooldownMs,
  });
  allowedThisBatch?.add(queryHash);
  return true;
}

/**
 * Open a forced-refetch batch spanning one applyEvent flush. While open, a
 * query hash granted a refetch and then re-matched by another invalidator in
 * the same batch is suppressed WITHOUT scheduling a trailing catch-up (the
 * query already refetches in this flush). Must be paired with
 * `endRefetchBatch` — call from a `try`/`finally` so the batch always closes.
 */
export function beginRefetchBatch(): void {
  allowedThisBatch = new Set();
}

/** Close the current forced-refetch batch opened by `beginRefetchBatch`. */
export function endRefetchBatch(): void {
  allowedThisBatch = null;
}

/**
 * Schedule the trailing catch-up invalidation. A single timer serves all
 * suppressed hashes and fires at the EARLIEST requested cooldown expiry, so
 * every suppressed query catches up no later than its own cooldown window
 * (at worst a later-suppressed query refetches slightly early — never late).
 *
 * Because call sites use different cooldowns (interactive 5s vs default 15s),
 * a request for a sooner expiry must move an already-scheduled timer earlier —
 * otherwise a 5s query suppressed after a 15s query pinned the timer would not
 * reconcile until the 15s mark.
 */
function scheduleCatchUp(queryClient: QueryClient, delayMs: number): void {
  // Track the most recent client so an org/client switch mid-cooldown
  // catches up against the live client.
  catchUpQueryClient = queryClient;
  const clampedDelay = Math.max(delayMs, 50);
  const fireAt = Date.now() + clampedDelay;
  if (catchUpTimer !== null) {
    // A timer is already pending; keep it unless this request needs to fire
    // sooner than the currently scheduled time.
    if (fireAt >= catchUpFireAt) {
      return;
    }
    clearTimeout(catchUpTimer);
  }
  catchUpFireAt = fireAt;
  catchUpTimer = setTimeout(runCatchUp, clampedDelay);
}

function runCatchUp(): void {
  catchUpTimer = null;
  catchUpFireAt = 0;
  const queryClient = catchUpQueryClient;
  const hashes = suppressedHashes;
  suppressedHashes = new Set();
  catchUpQueryClient = null;
  if (!queryClient || hashes.size === 0) {
    return;
  }
  const now = Date.now();
  queryClient.invalidateQueries({
    predicate: query => {
      if (!hashes.has(query.queryHash)) {
        return false;
      }
      // The catch-up refetch starts a fresh cooldown, reusing the cooldown
      // the original call site chose for this query.
      const cooldownMs =
        throttleEntries.get(query.queryHash)?.cooldownMs ??
        FORCED_REFETCH_COOLDOWN_MS;
      throttleEntries.set(query.queryHash, {
        allowedAgainAt: now + cooldownMs,
        cooldownMs,
      });
      return true;
    },
  });
}

/**
 * Immediately run any pending catch-up invalidation (test helper — mirrors
 * `flushInvalidations` in applyEvent.ts).
 */
export function flushRefetchThrottleCatchUp(): void {
  if (catchUpTimer !== null) {
    clearTimeout(catchUpTimer);
  }
  runCatchUp();
}

/** Reset all throttle state (test helper). */
export function __resetRefetchThrottleForTesting(): void {
  if (catchUpTimer !== null) {
    clearTimeout(catchUpTimer);
  }
  catchUpTimer = null;
  catchUpFireAt = 0;
  catchUpQueryClient = null;
  throttleEntries = new Map();
  suppressedHashes = new Set();
  allowedThisBatch = null;
}
